import ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import { AcceptInvitationResult, Invitation } from '@/features/invitations/types'
import * as Sentry from '@sentry/react'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

/**
 * Error thrown by every invitations service call on a non-2xx response. Carries
 * the HTTP `status` so callers can branch on it (403/404/409) without brittle
 * string matching on the message. Mirrors CaseConnectError's shape so the shared
 * `okJson` helper can throw it and callers can `instanceof`-check `.status`.
 */
export class InvitationError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'InvitationError'
    this.status = status
  }
}

function withCaller(path: string, caller: string): string {
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}caller=${encodeURIComponent(caller)}`
}

async function okJson<T>(response: Response, op: string): Promise<T> {
  if (!response.ok) {
    const error = new InvitationError(
      response.status,
      `Invitation request failed: ${response.status} ${response.statusText}`
    )
    // Operation-tag only — never the token or any invitation identifier (mirrors
    // caseConnect's PHI-safe Sentry pattern).
    Sentry.captureException(error, { tags: { operation: op } })
    throw error
  }
  return (await response.json()) as T
}

/**
 * List invitations for a Case Connect case. AuthZ (case:manage_access OR
 * org_admin) is enforced server-side; the raw token is never returned.
 */
export async function listCaseInvitations(caseId: string, caller: string): Promise<Invitation[]> {
  return Sentry.startSpan({ op: 'invitations.list', name: 'List Case Invitations' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.get(
      withCaller(`/invitations?targetType=CASE_CONNECT&targetId=${encodeURIComponent(caseId)}`, caller)
    )
    const data = await okJson<{ invitations: Invitation[] }>(response, 'invitations.list')
    return data.invitations ?? []
  })
}

/** Revoke a pending invitation (case:manage_access OR org_admin). */
export async function revokeInvitation(invitationId: string, caller: string): Promise<void> {
  return Sentry.startSpan({ op: 'invitations.revoke', name: 'Revoke Invitation' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(withCaller(`/invitations/${invitationId}/revoke`, caller), {}, JSON_HEADERS)
    await okJson(response, 'invitations.revoke')
  })
}

/**
 * Accept an invitation by its token. The server enforces that the invited email
 * matches the caller's email (403 otherwise); this returns only the caller's own
 * granted role/target.
 */
export async function acceptInvitation(token: string, caller: string): Promise<AcceptInvitationResult> {
  return Sentry.startSpan({ op: 'invitations.accept', name: 'Accept Invitation' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(
      withCaller(`/invitations/accept/${encodeURIComponent(token)}`, caller),
      {},
      JSON_HEADERS
    )
    return okJson<AcceptInvitationResult>(response, 'invitations.accept')
  })
}

/** Decline an invitation by its token. */
export async function declineInvitation(token: string, caller: string): Promise<void> {
  return Sentry.startSpan({ op: 'invitations.decline', name: 'Decline Invitation' }, async () => {
    const client = ProviderAPIClient.getInstance()
    const response = await client.post(
      withCaller(`/invitations/decline/${encodeURIComponent(token)}`, caller),
      {},
      JSON_HEADERS
    )
    await okJson(response, 'invitations.decline')
  })
}
